/**
 * PCM 헬퍼.
 *
 * 서버가 보내는 출력 오디오 프레임은 `[audioSeq uint32 LE][PCM16 24kHz mono LE]` 이다.
 */
import {
  AUDIO_SEQ_PREFIX_BYTES,
  OPUS_FRAME_HEADER_BYTES,
  OPUS_FRAME_MAGIC_0,
  OPUS_FRAME_MAGIC_1,
} from './protocol';

/** 이 플랫폼이 리틀엔디언인가. PCM은 항상 LE이므로 빅엔디언에서는 바이트를 뒤집어 읽는다. */
const IS_LITTLE_ENDIAN = (() => {
  const probe = new Uint8Array(new Uint16Array([1]).buffer);
  return probe[0] === 1;
})();

export interface DecodedAudioFrame {
  /** 오디오 청크 일련번호. 재접속 전후 공백 측정·로그 대조용 */
  seq: number;
  /** PCM16 샘플 */
  pcm: Int16Array<ArrayBuffer>;
}

/** 서버 바이너리 프레임에서 audioSeq와 PCM16을 분리한다. 길이가 모자라면 null. */
export function decodeAudioFrame(data: ArrayBuffer): DecodedAudioFrame | null {
  if (data.byteLength < AUDIO_SEQ_PREFIX_BYTES + 2) return null;
  const view = new DataView(data);
  const seq = view.getUint32(0, true);
  const sampleCount = Math.floor((data.byteLength - AUDIO_SEQ_PREFIX_BYTES) / 2);

  if (IS_LITTLE_ENDIAN) {
    // 접두 4바이트는 2의 배수라 Int16Array를 그대로 얹을 수 있다.
    return { seq, pcm: new Int16Array(data, AUDIO_SEQ_PREFIX_BYTES, sampleCount) };
  }
  const pcm = new Int16Array(sampleCount);
  for (let i = 0; i < sampleCount; i += 1) {
    pcm[i] = view.getInt16(AUDIO_SEQ_PREFIX_BYTES + i * 2, true);
  }
  return { seq, pcm };
}

/**
 * Opus 바이너리 프레임.
 *
 * `packets`는 입력 `ArrayBuffer` 위의 뷰라 복사가 없다. `EncodedAudioChunk`의 `data`는
 * `BufferSource`를 받으므로 그대로 넘길 수 있다.
 */
export interface DecodedOpusFrame {
  /** 이 프레임을 완성시킨 Gemini 청크의 seq. PCM seq의 단조 증가 부분집합이다 */
  seq: number;
  /** 20ms Opus 패킷들. 보낸 순서 그대로다 */
  packets: Array<Uint8Array<ArrayBuffer>>;
}

/**
 * Opus 프레임을 뜯는다. 규약에 조금이라도 맞지 않으면 **null**이다.
 *
 * 형식 구분 자체는 `audioFormat` 제어 메시지가 하고, 여기서 보는 매직 2바이트와
 * 길이 합 검사는 방어용이다 — 형식 경계를 잘못 읽었다면 잡음을 재생하느니 버린다.
 *
 * PCM 경로(`decodeAudioFrame`)는 이 함수와 무관하며 한 줄도 바뀌지 않았다.
 */
export function decodeOpusFrame(data: ArrayBuffer): DecodedOpusFrame | null {
  if (data.byteLength < OPUS_FRAME_HEADER_BYTES) return null;

  const bytes = new Uint8Array(data);
  if (bytes[4] !== OPUS_FRAME_MAGIC_0 || bytes[5] !== OPUS_FRAME_MAGIC_1) return null;
  // reserved(7번 바이트)는 **읽지 않는다.** 보내는 쪽은 0을 쓰고 받는 쪽은 무시해야
  // 나중에 이 자리를 쓸 때 구버전 뷰어가 깨지지 않는다.

  const view = new DataView(data);
  const seq = view.getUint32(0, true);
  const count = bytes[6] ?? 0;

  const dataStart = OPUS_FRAME_HEADER_BYTES + count * 2;
  if (data.byteLength < dataStart) return null;

  const lengths = new Array<number>(count);
  let payloadBytes = 0;
  for (let i = 0; i < count; i += 1) {
    const len = view.getUint16(OPUS_FRAME_HEADER_BYTES + i * 2, true);
    lengths[i] = len;
    payloadBytes += len;
  }
  // 남는 바이트도 모자란 바이트도 규약 위반이다.
  if (data.byteLength !== dataStart + payloadBytes) return null;

  const packets: Array<Uint8Array<ArrayBuffer>> = [];
  let off = dataStart;
  for (let i = 0; i < count; i += 1) {
    const len = lengths[i] ?? 0;
    packets.push(bytes.subarray(off, off + len));
    off += len;
  }
  return { seq, packets };
}

/** PCM16 → Float32 (-1 ~ 1). Web Audio의 AudioBuffer에 넣을 형태. */
export function int16ToFloat32(pcm: Int16Array<ArrayBuffer>): Float32Array<ArrayBuffer> {
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i += 1) {
    const v = pcm[i] ?? 0;
    out[i] = v < 0 ? v / 32768 : v / 32767;
  }
  return out;
}

/** 선형 진폭 → dBFS. 0은 최저값으로 내린다. */
export function toDbfs(amplitude: number, floorDb = -100): number {
  if (amplitude <= 0) return floorDb;
  return Math.max(floorDb, 20 * Math.log10(amplitude));
}
